import React, { FC } from 'react';
import PriceTerm from 'src/common/components/PriceTerm';
import getClassNames from 'src/common/utils/getClassNames';
import type { Price, PriceGroup, PriceSection } from 'src/common/types/common/PriceSection';
import type { Currency } from 'src/common/types/common/Currency';
import CurrencySelectorInput from 'src/common/components/CurrencySelectorInput';
import classNames from 'classnames';
import CostDetails from './CostDetails';
import {
  Theme, themes, Variant, variants,
} from './types';
import styles from './CostBreakdown.module.scss';

export type CostBreakdownProps = {
  open?: boolean;
  priceSection?: PriceSection;
  subHeading?: React.ReactNode;
  renterCurrency?: Currency;
  theme?: Theme;
  showCurrencySelector?: boolean;
  withHeader?: boolean;
  variant?: Variant;
  withDottedFooterLine?: boolean;
  noPadding?: boolean;
  showFooter?: boolean;
  noPricesMarginBottom?: boolean;
};

const renderPriceLines = (priceGroup: PriceGroup): React.ReactNode => priceGroup?.price_lines
  ?.map((price: Price) => (
    <React.Fragment key={price.id + price.label}>
      <dt className={styles.priceLabel}>
        <PriceTerm
          classNameModifier="payoutBreakdown"
          tooltipContent={price.help}
        >
          {price.label}
        </PriceTerm>
      </dt>
      <dd
        className={styles.priceValue}
        data-test={`${price.label.replaceAll(' ', '-').toLowerCase()}-amount`}
      >
        {price.formatted_amount}
      </dd>
    </React.Fragment>
  ));

const CostBreakdown: FC<CostBreakdownProps> = ({
  open = true,
  priceSection,
  renterCurrency,
  theme = themes[0],
  showCurrencySelector = false,
  withHeader = true,
  variant = variants[0],
  withDottedFooterLine = false,
  noPadding = false,
  subHeading,
  showFooter = true,
  noPricesMarginBottom = false,
}) => {
  const footerLabel = priceSection?.footer_label;
  const showFooterContent = showFooter
    && (!!footerLabel || (showCurrencySelector && renterCurrency));
  const priceGroupsNumber = priceSection?.price_groups?.length ?? 0;
  const multilevel = priceGroupsNumber > 1;
  const withCurrencySelector = !!(showCurrencySelector && renterCurrency);

  return (
    <CostDetails
      amount={priceSection?.formatted_amount ?? ''}
      open={open}
      theme={theme}
      title={priceSection?.header_label ?? ''}
      subHeading={subHeading}
      variant={variant}
      withHeader={withHeader}
      noPadding={noPadding}
    >
      {priceSection?.price_groups?.map((priceGroup: PriceGroup) => (multilevel ? (
        <CostDetails
          amount={priceGroup.formatted_amount}
          key={priceGroup.id}
          open={false}
          title={priceGroup.label}
          variant="secondary"
          noPadding={noPadding}
        >
          <dl className={getClassNames(styles, 'prices', classNames('multilevel', { noMarginBottom: noPricesMarginBottom }))}>
            {renderPriceLines(priceGroup)}
          </dl>
        </CostDetails>
      ) : (
        <dl
          key={priceGroup.id}
          className={getClassNames(styles, 'prices', classNames({ noMarginBottom: noPricesMarginBottom }))}
        >
          {renderPriceLines(priceGroup)}
        </dl>
      )))}

      {showFooterContent && (
        <div className={getClassNames(styles, 'footer', classNames({ dottedTopBorder: withDottedFooterLine }))}>
          {!!footerLabel
            && (
              <div className={styles.total}>
                <span>{footerLabel}</span>
                <span data-test="total-amount">
                  {priceSection.formatted_amount}
                </span>
              </div>
            )}
          {withCurrencySelector && (
            <CurrencySelectorInput variant="dropdownField" />
          )}
        </div>
      )}
    </CostDetails>
  );
};

export default CostBreakdown;
